import { watch } from 'vue'
import { useQuery, useMutation } from '@tanstack/vue-query'
import { $axios, $queryClient } from '@/boot/initApi'
import { enrichNotification } from '@/helpers/notificationHelpers'

const QUERY_KEY = ['notifications']

const cancelAndSnapshot = async () => {
  await $queryClient.cancelQueries({ queryKey: QUERY_KEY, exact: true })
  return $queryClient.getQueryData(QUERY_KEY) ?? []
}

const invalidate = () => $queryClient.invalidateQueries({ queryKey: QUERY_KEY })

export default ({ items, checkedNotificationIds }) => {
  const revert = (_, __, ctx) => {
    $queryClient.setQueryData(QUERY_KEY, ctx.previous)
    if (!ctx.previousChecked) return
    checkedNotificationIds.value.clear()
    ctx.previousChecked.forEach(id => checkedNotificationIds.value.add(id))
  }

  return {
    indexNotifications: ({ enabled = true } = {}) => {
      const query = useQuery({
        queryKey: QUERY_KEY,
        queryFn: async () => {
          try {
            const { data } = await $axios.get('api/notifications')
            return data
          } catch (err) {
            console.error(err)
            return []
          }
        },
        enabled
      }, $queryClient)

      watch(query.data, val => {
        if (val) items.value = val.map(enrichNotification)
      }, { immediate: true })

      return query
    },

    markNotificationAsRead: () => useMutation({
      mutationFn: id => $axios.patch(`api/notifications/mark-as-read/${id}`),
      onMutate: async id => {
        const previous = await cancelAndSnapshot()
        $queryClient.setQueryData(QUERY_KEY, previous.map(n =>
          n.id === id ? { ...n, readAt: new Date().toISOString() } : n
        ))
        return { previous }
      },
      onError: revert,
      onSettled: invalidate
    }, $queryClient),

    markNotificationAsUnread: () => useMutation({
      mutationFn: id => $axios.patch(`api/notifications/mark-as-unread/${id}`),
      onMutate: async id => {
        const previous = await cancelAndSnapshot()
        $queryClient.setQueryData(QUERY_KEY, previous.map(n =>
          n.id === id ? { ...n, readAt: null } : n
        ))
        return { previous }
      },
      onError: revert,
      onSettled: invalidate
    }, $queryClient),

    destroyNotification: () => useMutation({
      mutationFn: id => $axios.delete(`api/notifications/destroy/${id}`),
      onMutate: async id => {
        const previous = await cancelAndSnapshot()
        $queryClient.setQueryData(QUERY_KEY, previous.filter(n => n.id !== id))
        const previousChecked = new Set(checkedNotificationIds.value)
        checkedNotificationIds.value.delete(id)
        return { previous, previousChecked }
      },
      onError: revert,
      onSettled: invalidate
    }, $queryClient),

    markAllNotificationsAsRead: () => useMutation({
      mutationFn: () => $axios.patch('api/notifications/mark-as-read'),
      onMutate: async () => {
        const previous = await cancelAndSnapshot()
        const readAt = new Date().toISOString()
        $queryClient.setQueryData(QUERY_KEY, previous.map(n => ({ ...n, readAt })))
        return { previous }
      },
      onError: revert,
      onSettled: invalidate
    }, $queryClient),

    markAllNotificationsAsUnread: () => useMutation({
      mutationFn: () => $axios.patch('api/notifications/mark-as-unread'),
      onMutate: async () => {
        const previous = await cancelAndSnapshot()
        $queryClient.setQueryData(QUERY_KEY, previous.map(n => ({ ...n, readAt: null })))
        return { previous }
      },
      onError: revert,
      onSettled: invalidate
    }, $queryClient),

    destroyAllNotifications: () => useMutation({
      mutationFn: () => $axios.delete('api/notifications/destroy'),
      onMutate: async () => {
        const previous = await cancelAndSnapshot()
        $queryClient.setQueryData(QUERY_KEY, [])
        const previousChecked = new Set(checkedNotificationIds.value)
        checkedNotificationIds.value.clear()
        return { previous, previousChecked }
      },
      onError: revert,
      onSettled: invalidate
    }, $queryClient),

    destroyAllReadNotifications: () => useMutation({
      mutationFn: () => $axios.delete('api/notifications/destroy-read'),
      onMutate: async () => {
        const previous = await cancelAndSnapshot()
        $queryClient.setQueryData(QUERY_KEY, [])
        const previousChecked = new Set(checkedNotificationIds.value)
        checkedNotificationIds.value.clear()
        return { previous, previousChecked }
      },
      onError: revert,
      onSettled: invalidate
    }, $queryClient),

    destroyCheckedNotifications: () => useMutation({
      mutationFn: ids => $axios.delete('api/notifications/destroy-bulk', { data: { ids } }),
      onMutate: async ids => {
        const previous = await cancelAndSnapshot()
        const idSet = new Set(ids)
        $queryClient.setQueryData(QUERY_KEY, previous.filter(n => !idSet.has(n.id)))
        const previousChecked = new Set(checkedNotificationIds.value)
        checkedNotificationIds.value.clear()
        return { previous, previousChecked }
      },
      onError: revert,
      onSettled: invalidate
    }, $queryClient),

    toggleNotificationChecked: id => {
      if (checkedNotificationIds.value.has(id)) checkedNotificationIds.value.delete(id)
      else checkedNotificationIds.value.add(id)
    },

    clearCheckedNotifications: () => checkedNotificationIds.value.clear()
  }
}
